import { FC, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router';
import i18next from 'i18next';
import { AxiosError } from 'axios';
import { CLIENT_LOGIN_LINK } from 'commons/constants/links';
import PhoneNumberField from 'commons/components/fields/PhoneNumberField/PhoneNumberField';
import { storeName } from 'store/models/user.model';
import { storeName as layoutModelName } from 'store/models/layout.model';
import LanguageSwitcher from 'commons/components/LanguageSwitcher';
import { RootState } from 'store/store';
import { registerClient, verifyEmailRegistrationOtp, verifyPhoneRegistrationOtp } from 'services/users';
import './styles.scss';

const GOOGLE_OAUTH_URL = `${process.env.REACT_APP_OAUTH_DOMAIN}/oauth2/authorize/google`;

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
    <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"/>
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
  </svg>
);

interface FormState {
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
}

type RegisterMode = 'EMAIL' | 'PHONE';

const ClientSignUpPage: FC = () => {
  const language = (() => { try { return localStorage.getItem('i18nextLng') || 'vi-VN'; } catch (_) { return 'vi-VN'; } })();
  i18next.changeLanguage(language);

  const user = useSelector((state: RootState) => state[storeName].user);
  const layoutDispatch = useDispatch()[layoutModelName];

  const [form, setForm] = useState<FormState>({ name: '', email: '', phoneNumber: '', password: '', confirmPassword: '' });
  const [mode, setMode] = useState<RegisterMode>('EMAIL');
  const [otpCode, setOtpCode] = useState('');
  const [otpPhoneNumber, setOtpPhoneNumber] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  layoutDispatch.setLoading(false);

  if (user) {
    return <Redirect to="/" />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(null);
  };

  const handlePhoneChange = (value?: string) => {
    setForm({ ...form, phoneNumber: value || '' });
    setError(null);
  };

  const translateServerMessage = (message?: string) => {
    if (message === 'Phone number unavailable') {
      return i18next.t('client-signup.phone-unavailable');
    }
    if (message === 'Email unavailable') {
      return i18next.t('client-signup.email-unavailable');
    }
    if (message === 'Email invalid') {
      return i18next.t('client-signup.email-invalid');
    }
    if (message === 'Email required') {
      return i18next.t('common.error.message-error-email');
    }
    if (message === 'Display name required') {
      return i18next.t('client-signup.name-required');
    }
    if (message === 'No pending email registration found') {
      return i18next.t('client-signup.email-registration-not-found');
    }
    if (message === 'Email verification code invalid') {
      return i18next.t('client-signup.email-otp-invalid');
    }
    if (message === 'OTP has expired. Please request a new OTP.') {
      return i18next.t('client-signup.otp-expired');
    }
    if (message === 'Too many failed attempts. Please request a new OTP.') {
      return i18next.t('client-signup.otp-too-many-attempts');
    }
    if (message === 'No pending OTP found. Please request a new OTP.') {
      return i18next.t('client-signup.otp-not-found');
    }
    return message;
  };

  const getErrorMessage = (err: unknown, fallback: string) => {
    const axiosError = err as AxiosError;
    const data = axiosError.response?.data as { message?: string };
    return translateServerMessage(data?.message) || fallback;
  };

  const resetNotice = () => {
    setError(null);
    setSuccess(false);
  };

  const handleModeChange = (nextMode: RegisterMode) => {
    setMode(nextMode);
    setOtpStep(false);
    setOtpCode('');
    setOtpEmail('');
    setOtpPhoneNumber('');
    resetNotice();
  };

  const submitRegistration = async () => {
    resetNotice();
    if (form.password !== form.confirmPassword) {
      setError(i18next.t('client-signup.password-not-match'));
      return;
    }

    if (!form.name.trim()) {
      setError(i18next.t('client-signup.name-required'));
      return;
    }

    if (mode === 'EMAIL' && !form.email.trim()) {
      setError(i18next.t('common.error.message-error-email'));
      return;
    }
    if (mode === 'PHONE' && !form.phoneNumber.trim()) {
      setError(i18next.t('common.error.message-error-phoneNumber'));
      return;
    }

    setLoading(true);
    try {
      await registerClient({
        registerMethod: mode,
        name: form.name,
        email: mode === 'EMAIL' ? form.email : undefined,
        phoneNumber: mode === 'PHONE' ? form.phoneNumber : undefined,
        password: form.password,
        confirmPassword: form.confirmPassword,
        userLocale: language.replace('-', '_'),
      });
      if (mode === 'PHONE') {
        setOtpPhoneNumber(form.phoneNumber);
        setOtpStep(true);
        setSuccess(true);
        return;
      }
      setOtpEmail(form.email);
      setOtpStep(true);
      setSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err, i18next.t('client-signup.error-register')));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitRegistration();
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    resetNotice();
    if (!otpCode.trim()) {
      setError(i18next.t('client-signup.otp-required'));
      return;
    }
    setLoading(true);
    try {
      const res = mode === 'EMAIL'
        ? await verifyEmailRegistrationOtp({ email: otpEmail || form.email, otpCode })
        : await verifyPhoneRegistrationOtp({ phoneNumber: otpPhoneNumber || form.phoneNumber, otpCode });
      if (res.data?.success) {
        window.location.href = CLIENT_LOGIN_LINK;
        return;
      }
      const attempts = res.data?.attemptsRemaining;
      setError(
        attempts !== undefined
          ? `${translateServerMessage(res.data.message) || res.data.message} ${i18next.t('client-signup.otp-attempts', { count: attempts })}`
          : translateServerMessage(res.data?.message)
      );
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          i18next.t(mode === 'EMAIL' ? 'client-signup.error-email-otp' : 'client-signup.error-otp')
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="csu-page">
      <div className="csu-card">
          <LanguageSwitcher />

          <h1 className="csu-heading">{i18next.t('client-signup.title')}</h1>
          <p className="csu-subtitle">
            {i18next.t('client-signup.subtitle')} {process.env.REACT_APP_PRODUCT_NAME || 'Clik Platform'}
          </p>

          {success && <p className="csu-success">{i18next.t('client-signup.notify-success')}</p>}

          {!otpStep && (
            <div className="csu-mode" role="tablist">
              <button
                className={`csu-mode-btn${mode === 'EMAIL' ? ' csu-mode-btn--active' : ''}`}
                type="button"
                onClick={() => handleModeChange('EMAIL')}
              >
                {i18next.t('client-signup.option-email')}
              </button>
              <button
                className={`csu-mode-btn${mode === 'PHONE' ? ' csu-mode-btn--active' : ''}`}
                type="button"
                onClick={() => handleModeChange('PHONE')}
              >
                {i18next.t('client-signup.option-phone')}
              </button>
            </div>
          )}

          {otpStep ? (
            <form onSubmit={handleVerifyOtp} noValidate>
              <div className="csu-field">
                <label className="csu-label" htmlFor="csu-otp">{i18next.t('client-signup.field-otp')}</label>
                <input
                  id="csu-otp"
                  className="csu-input csu-input--otp"
                  name="otpCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => {
                    setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setError(null);
                  }}
                  required
                />
                <span className="csu-hint">
                  {mode === 'EMAIL'
                    ? i18next.t('client-signup.email-otp-sent', { email: otpEmail })
                    : i18next.t('client-signup.otp-sent', { phone: otpPhoneNumber })}
                </span>
              </div>

              {error && <span className="csu-error">{error}</span>}

              <button className="csu-btn-primary" type="submit" disabled={loading}>
                {loading ? i18next.t('client-signup.btn-verifying') : i18next.t('client-signup.btn-verify-otp')}
              </button>
              <button className="csu-btn-secondary" type="button" disabled={loading} onClick={() => submitRegistration()}>
                {i18next.t('client-signup.btn-resend')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="csu-field">
                <label className="csu-label" htmlFor="csu-name">
                  {i18next.t('client-signup.field-name')}
                </label>
                <input
                  id="csu-name"
                  className="csu-input"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>

              {mode === 'EMAIL' ? (
                <div className="csu-field">
                  <label className="csu-label" htmlFor="csu-email">{i18next.t('client-signup.field-email')}</label>
                  <input
                    id="csu-email"
                    className="csu-input"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              ) : (
                <div className="csu-field">
                  <label className="csu-label" htmlFor="csu-phone">{i18next.t('client-signup.field-phone')}</label>
                  <PhoneNumberField
                    placeholder="+84 xxx xxx xxx"
                    value={form.phoneNumber}
                    onChange={handlePhoneChange}
                  />
                </div>
              )}

              <div className="csu-field">
                <label className="csu-label" htmlFor="csu-password">{i18next.t('client-signup.field-password')}</label>
                <input
                  id="csu-password"
                  className="csu-input"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
                <span className="csu-hint">{i18next.t('client-signup.field-password-hint')}</span>
              </div>

              <div className="csu-field">
                <label className="csu-label" htmlFor="csu-confirm-password">{i18next.t('client-signup.field-confirm-password')}</label>
                <input
                  id="csu-confirm-password"
                  className="csu-input"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>

              {error && <span className="csu-error">{error}</span>}

              <button className="csu-btn-primary" type="submit" disabled={loading}>
                {loading ? i18next.t('client-signup.btn-submitting') : i18next.t('client-signup.btn-submit')}
              </button>
            </form>
          )}

          <div className="csu-divider"><span>{i18next.t('client-signup.divider')}</span></div>

          <a className="csu-btn-google" href={GOOGLE_OAUTH_URL}>
            <GoogleIcon />
            {i18next.t('client-signup.btn-google')}
          </a>

          <p className="csu-footer">
            {i18next.t('client-signup.footer-text')}&nbsp;<a href={CLIENT_LOGIN_LINK}>{i18next.t('client-signup.footer-link')}</a>
          </p>
      </div>
    </div>
  );
};

export default ClientSignUpPage;
